import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { operationsClient } from '@/lib/api/client';
import { authEndpoints } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/errors';
import { useAuthActions, useAuthStore } from '@/stores/auth.store';
import { queryClient } from '@/lib/queryClient';

// ─── Session leak prevention ─────────────────────────────────────────────────
//
// When a user finishes a password reset (or sets a password from an invitation
// link), the browser may still hold the *previous* user's session: the auth
// store has their token + permissions, react-query has their cached data,
// localStorage has the persisted auth-store payload + the `notifi:last-email`
// hint. If we navigate them to `/login` without scrubbing, the next login
// briefly renders the previous user's dashboard before the new token's data
// loads — a cross-user session leak (think: admin invites user → user clicks
// link on shared device → sees admin's view post-login).
//
// `clearSession` is the single chokepoint that wipes everything tied to a
// session. It's invoked from the password-reset success path AND from the
// login mutation (so a fresh login on a shared device always starts clean).
// Keep the helper free of React/hook semantics so it can run from a mutationFn
// or a route loader without ceremony.

/** localStorage keys the auth flow writes outside the Zustand persist payload. */
const AUTH_LOCAL_STORAGE_KEYS = [
  // The Zustand-persisted snapshot of the auth store. Even though logout()
  // clears the in-memory state, the persist middleware writes the empty
  // snapshot back asynchronously; clobbering the key here removes the row
  // synchronously so a hard reload before the next write can't resurrect it.
  'auth-store',
  // Last-login-email hint (see login.tsx + auth.store.ts).
  'notifi:last-email',
] as const;

/**
 * Nukes every trace of the current user's session: zustand auth state, the
 * react-query cache (so previously-fetched per-user data can't leak into the
 * next session), and the auth-related localStorage keys we write. Cookie-bound
 * tokens are server-managed and will be replaced by the next login response,
 * so we don't touch document.cookie here.
 *
 * Safe to call when there's no active session — `logout()` is idempotent and
 * the localStorage removals are best-effort.
 */
export function clearSession(): void {
  // 1. Zustand store. logout() also tears down socket/voip/tenant stores.
  useAuthStore.getState().logout();
  // 2. React-Query cache. Without this, any cached list/detail keyed off the
  //    previous tenant/user (jobs, contacts, etc.) is returned instantly to
  //    the next user before fresh fetches can replace it.
  queryClient.clear();
  // 3. localStorage. logout() already removes `notifi:last-email`, but list
  //    all keys explicitly so this helper is the single source of truth and
  //    future additions land here. localStorage can throw in private mode —
  //    best-effort only.
  for (const key of AUTH_LOCAL_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  userId: string;
  tenantId: string;
  roles: string[];
  permissions: string[];
  permissionsVersion: number;
  permissionScopes?: Record<string, string>;
  capabilityFlags?: Record<string, boolean>;
  fieldMasks?: string[];
  technicianId?: string | null;
}

interface OperationsLoginResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  actor: {
    userId: string;
    tenantId: string;
    roles: string[];
    permissions: string[];
    permissionsVersion: number;
    permissionScopes?: Record<string, string>;
    capabilityFlags?: Record<string, boolean>;
    fieldMasks?: string[];
    sessionId: string;
    technicianId?: string | null;
  };
}

export function useLogin() {
  return useApiMutation<LoginResponse, LoginRequest>({
    mutationFn: async (data) => {
      // Wipe ANY previous session before authenticating. Shared-device case:
      // user A is signed in, walks away; user B opens /login and signs in. If
      // we don't clear first, user A's zustand state + react-query cache leak
      // into user B's first render. Doing this in the mutationFn (rather than
      // onSuccess) is intentional: it must run BEFORE the login network call
      // so the request itself can't read user A's stale tenant header out of
      // the store.
      clearSession();
      // No tenant header on login: a user belongs to exactly one tenant, so
      // the backend resolves the tenant from the email and returns its id.
      // That id is stored below and attached to every later request.
      const res = await operationsClient.post<OperationsLoginResponse>(authEndpoints.login(), data);
      const d = res.data;
      return {
        token: d.accessToken,
        // Held in memory only (auth.store does not persist it) and spent by
        // lib/api/session.ts to renew the 8-hour access token.
        refreshToken: d.refreshToken ?? '',
        userId: d.actor.userId,
        tenantId: d.actor.tenantId,
        roles: d.actor.roles,
        permissions: d.actor.permissions,
        permissionsVersion: d.actor.permissionsVersion,
        permissionScopes: d.actor.permissionScopes,
        capabilityFlags: d.actor.capabilityFlags,
        fieldMasks: d.actor.fieldMasks,
        technicianId: d.actor.technicianId ?? null,
      };
    },
    showErrorToast: false,
  });
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface PasswordLinkValidation {
  success: true;
  kind: 'password-reset' | 'invite';
}

export function usePasswordResetTokenValidation(token: string) {
  return useApiQuery<PasswordLinkValidation>({
    queryKey: ['auth', 'password-reset-token', token],
    queryFn: async ({ signal }) => {
      try {
        await operationsClient.post<{ success: true }>(
          authEndpoints.validateResetToken(),
          { token },
          { signal },
        );
        return { success: true, kind: 'password-reset' };
      } catch (resetError) {
        if (!(resetError instanceof ApiError) || resetError.status !== 400) {
          throw resetError;
        }

        // Some invitation emails issued before this route was corrected use
        // /create-password/:token with an AuthInvite token. Preserve those
        // still-live links by identifying them and routing to /invite below.
        try {
          await operationsClient.get(authEndpoints.inviteContext(token), { signal });
          return { success: true, kind: 'invite' };
        } catch (inviteError) {
          if (
            inviteError instanceof ApiError &&
            (inviteError.status === 400 || inviteError.status === 409)
          ) {
            throw resetError;
          }
          throw inviteError;
        }
      }
    },
    retry: false,
    gcTime: 0,
    showErrorToast: false,
  });
}

export function useForgotPassword() {
  // Targets operations (PR #469 wired SendGrid delivery there). The endpoint
  // is tenant-optional: the public form has no tenant context, and the
  // backend derives the tenant from the user looked up by email.
  return useApiMutation<void, ForgotPasswordRequest>({
    mutationFn: async (data) => {
      await operationsClient.post(authEndpoints.requestPasswordReset(), data);
    },
    showErrorToast: false,
  });
}

export function useResetPassword() {
  // Targets operations. Tenant is derived from the reset token row, so no
  // `x-tenant-id` header is required. The operations DTO names the new
  // password `newPassword`, so we rename here at the boundary rather than
  // leaking the wire shape into UI types.
  //
  // SESSION LEAK FIX: on success we MUST wipe the prior session. Repro:
  // admin signed in → invites a new user → invited user (or admin themselves
  // on the same browser) clicks the link → sets password → navigates to
  // /login. Without clearSession() here, the admin's tokens + zustand state
  // + react-query cache survive into /login and the new user's post-login
  // first render shows the admin's data. Clearing on the mutation's success
  // path (not onError) is deliberate: a failed reset never authenticated as
  // anyone new, so the existing session should remain intact for retry.
  return useApiMutation<void, ResetPasswordRequest>({
    mutationFn: async (data) => {
      await operationsClient.post(authEndpoints.resetPassword(), {
        token: data.token,
        newPassword: data.password,
      });
    },
    onSuccess: () => {
      clearSession();
    },
    showErrorToast: false,
  });
}

export function useLogout() {
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  return useCallback(() => {
    logout();
    navigate({ to: '/login', replace: true });
  }, [logout, navigate]);
}
